/**
 * TODO: Make whatever you can of these into behaviors
 */
import { useEffect } from 'react';
import debounce from 'lodash/debounce';

import noop from 'utils/noop';
import { EditorEvent, GraphCommonEvent, ItemState } from '../../../constants';
import { EditorContextProps, withEditorContext } from '../../../hooks';
import {
  CanvasProp,
  deleteUtils,
  hideTooltip,
  shapeNames,
  updateSelectedChildNodes,
} from '../../../shapes';
import { CreateEdgeEvent, IG6GraphEvent, Item, NodeSelectChangeEvent } from '../../../types';
import {
  doesItemExist,
  getSelectedNodes,
  isNode,
  normalizeItemArray,
  normalizeNodeEventData,
  showInlineEdit,
} from '../../../utils';
import { FlowChartProps } from '../FlowChart.types';

/**
 * This hooks up listeners with the graph commands
 * It is a component instead of a utility function so that it can utilize
 * gg-editors internal commands
 */
export const FlowChartListeners = withEditorContext(
  ({
    graph,
    nodes = [],
    onAdd = noop,
    onAfterRelayout = noop,
    onBeforeRelayout = noop,
    onDelete = noop,
    onNodePositionChange = noop,
    onNodeSelectChange = noop,
    onNodeDblClick = noop,
    onItemMouseDown = noop,
    onButtonClick = noop,
  }: EditorContextProps & Partial<FlowChartProps>) => {
    // Delete Callbacks
    const handleDeleteClick = ({ item }: IG6GraphEvent) => {
      if (!graph || !doesItemExist(graph, item)) return;

      const itemIsNode = isNode(item);
      let deletedNodes: Item[] = [];
      let deletedEdges: Item[] = [];

      if (itemIsNode) {
        deletedNodes = [item];
        // Gets edges connected to this node
        deletedEdges = item.getEdges() || [];
      } else if (item !== null) {
        deletedEdges = [item];
      }

      onDelete({
        nodes: normalizeItemArray(deletedNodes),
        edges: normalizeItemArray(deletedEdges),
      });
    };

    const handleDeleteMouseEnter = (e: IG6GraphEvent) =>
      deleteUtils.setHoverFill(e.shape.getParent());
    const handleDeleteMouseLeave = (e: IG6GraphEvent) =>
      deleteUtils.setBaseFill(e.shape.getParent());

    // Link Callbacks
    const handleLinkIconMouseEnter = ({ shape }: IG6GraphEvent) =>
      shape.attr('img', shape.get('attrs').hoverImg);
    const handleLinkIconMouseLeave = ({ shape }: IG6GraphEvent) =>
      shape.attr('img', shape.get('attrs').baseImg);

    // Anchor Callbacks
    const handleAnchorMouseEnter = (e: IG6GraphEvent) =>
      e.shape.attr({
        stroke: e.shape.get('attrs').activeBorderColor,
        lineWidth: e.shape.get('attrs').activeLineWidth,
      });
    const handleAnchorMouseLeave = (e: IG6GraphEvent) =>
      e.shape.attr({
        stroke: e.shape.get('attrs').defaultBorderColor,
        lineWidth: e.shape.get('attrs').defaultLineWidth,
      });

    // Edge Callbacks
    const handleAfterCreateEdge = ({ edge }: CreateEdgeEvent) =>
      onAdd({ edges: normalizeItemArray([edge]) });

    // Node Callbacks
    const handleNodeDragEnd = ({ item }: IG6GraphEvent) => {
      if (!item) return;
      /**
       * Even multiple nodes are dragged, `e.item` only refers to one of them.
       * To make sure we get all the nodes, we're going to:
       *   1. Check if the `e.item` is selected
       *   2. If it is selected, return selected nodes
       *   3. If it is not, return `e.item`
       */
      const movedNodes = item.hasState('selected') ? getSelectedNodes(graph) : [item];

      onNodePositionChange({ nodes: normalizeItemArray(movedNodes) });
    };

    const handleNodeSelectChange = ({ selectedItems }: NodeSelectChangeEvent) => {
      updateSelectedChildNodes(graph);

      onNodeSelectChange(
        Object.entries(selectedItems).reduce((acc, [name, items]) => {
          return {
            ...acc,
            [name]: normalizeItemArray(items),
          };
        }, {}),
      );
    };

    const handleLayoutChange = () => {
      if (!graph) return;
      graph.destroyLayout();
      onNodePositionChange({ nodes: normalizeItemArray(graph.getNodes()) });
      onAfterRelayout();
    };

    useEffect(() => {
      const handleNodeDblClick = (e: IG6GraphEvent) => {
        const parsedData = normalizeNodeEventData(e);
        if (parsedData) {
          onNodeDblClick(parsedData);
        }
      };
      graph?.on('node:dblclick', handleNodeDblClick);

      return () => {
        graph?.off('node:dblclick', handleNodeDblClick);
      };
    }, [graph, onNodeDblClick]);

    useEffect(() => {
      if (onItemMouseDown === noop || !graph) return;

      graph.on('node:click', onItemMouseDown);
      graph.on('node:dragend', onItemMouseDown);
      graph.on(`${shapeNames.delete}:click`, onItemMouseDown);

      return () => {
        graph.off('node:click', onItemMouseDown);
        graph.off('node:dragend', onItemMouseDown);
        graph.off(`${shapeNames.delete}:click`, onItemMouseDown);
      };
    }, [graph, onItemMouseDown]);

    const handleNumOfUsersMouseEnter = (e: IG6GraphEvent) => {
      userMouseEvent(e, ItemState.HoverNumOfUser, true);
    };
    const handleNumOfUsersMouseleave = (e: IG6GraphEvent) => {
      userMouseEvent(e, ItemState.HoverNumOfUser, false);
    };
    const handleNumOfUsersEnteredMouseEnter = (e: IG6GraphEvent) => {
      userMouseEvent(e, ItemState.HoverNumOfUserEntered, true);
    };
    const handleNumOfUsersEnteredMouseleave = (e: IG6GraphEvent) => {
      userMouseEvent(e, ItemState.HoverNumOfUserEntered, false);
    };
    const handleNumOfUsersExitedMouseEnter = (e: IG6GraphEvent) => {
      userMouseEvent(e, ItemState.HoverNumOfUserExited, true);
    };
    const handleNumOfUsersExitedMouseleave = (e: IG6GraphEvent) => {
      userMouseEvent(e, ItemState.HoverNumOfUserExited, false);
    };

    const userMouseEvent = (e: IG6GraphEvent, state: ItemState, boolVal: boolean) => {
      const nodeItem = e.item;
      graph?.setItemState(nodeItem as Item, state, boolVal);
    };

    useEffect(() => {
      if (!graph) return;
      const handleButtonClick = (e: IG6GraphEvent) => {
        const { target } = e;
        onButtonClick({
          ...normalizeNodeEventData(e),
          target,
          buttonType: target.get('buttonType'),
        });
      };

      graph.on(`${shapeNames.linkIcon}:click`, handleButtonClick);
      graph.on(`${shapeNames.numUsersGroup}:click`, handleButtonClick);
      graph.on(`${shapeNames.numUsersEnteredGroup}:click`, handleButtonClick);
      graph.on(`${shapeNames.numUsersExitedGroup}:click`, handleButtonClick);
      graph.on(`${shapeNames.segmentButtonLink}:click`, handleButtonClick);

      return () => {
        if (graph) {
          graph.off(`${shapeNames.linkIcon}:click`, handleButtonClick);
          graph.off(`${shapeNames.numUsersGroup}:click`, handleButtonClick);
          graph.off(`${shapeNames.numUsersEnteredGroup}:click`, handleButtonClick);
          graph.off(`${shapeNames.numUsersExitedGroup}:click`, handleButtonClick);
          graph.off(`${shapeNames.segmentButtonLink}:click`, handleButtonClick);
        }
      };
    }, [graph, onButtonClick]);

    useEffect(() => {
      if (!graph) return;

      const hideTooltipIfShown = () => {
        const canvas = graph.get('canvas');
        if (canvas?.get(CanvasProp.Tooltip)) {
          hideTooltip(canvas);
        }
      };

      const debounceHideTooltip = debounce(hideTooltipIfShown, 300, {
        leading: true,
        trailing: false,
      });

      // Graph Listeners
      graph.on(GraphCommonEvent.onViewportChange, debounceHideTooltip);
      graph.on(GraphCommonEvent.onAfterModeChange, debounceHideTooltip);
      /**
       * The graphs default 'afterrelayout' event also fires on the initial
       * render.  Using our own avoids side effects due to this.
       */
      // @ts-ignore
      graph.on(EditorEvent.onAfterRelayout, handleLayoutChange);
      // @ts-ignore
      graph.on(EditorEvent.onBeforeRelayout, onBeforeRelayout);
      // Delete Listeners
      // @ts-ignore
      graph.on(`${shapeNames.delete}:click`, handleDeleteClick);
      // @ts-ignore
      graph.on(`${shapeNames.delete}:mouseenter`, handleDeleteMouseEnter);
      // @ts-ignore
      graph.on(`${shapeNames.delete}:mouseleave`, handleDeleteMouseLeave);
      // Link Listeners
      // @ts-ignore
      graph.on(`${shapeNames.linkIcon}:mouseenter`, handleLinkIconMouseEnter);
      // @ts-ignore
      graph.on(`${shapeNames.linkIcon}:mouseleave`, handleLinkIconMouseLeave);
      // Anchor Listeners
      // @ts-ignore
      graph.on(`${shapeNames.anchorOut}:mouseenter`, handleAnchorMouseEnter);
      // @ts-ignore
      graph.on(`${shapeNames.anchorOut}:mouseleave`, handleAnchorMouseLeave);
      // Number of Users Listeners
      // @ts-ignore
      graph.on(`${shapeNames.numUsersGroup}:mouseenter`, handleNumOfUsersMouseEnter);
      // @ts-ignore
      graph.on(`${shapeNames.numUsersGroup}:mouseleave`, handleNumOfUsersMouseleave);
      // @ts-ignore
      graph.on(`${shapeNames.numUsersEnteredGroup}:mouseenter`, handleNumOfUsersEnteredMouseEnter);
      // @ts-ignore
      graph.on(`${shapeNames.numUsersEnteredGroup}:mouseleave`, handleNumOfUsersEnteredMouseleave);
      // @ts-ignore
      graph.on(`${shapeNames.numUsersExitedGroup}:mouseenter`, handleNumOfUsersExitedMouseEnter);
      // @ts-ignore
      graph.on(`${shapeNames.numUsersExitedGroup}:mouseleave`, handleNumOfUsersExitedMouseleave);

      // Edge Listeners
      graph.on('aftercreateedge', handleAfterCreateEdge);
      /**
       * Node Listeners
       *
       * NOTE: After G6 is updated, this can be changed to use `dragnodeend`,
       * which will make the logic much less complex
       * https://g6.antv.vision/en/docs/api/Event#dragnodeend
       */
      graph.on('node:dragend', handleNodeDragEnd);
      // @ts-ignore
      graph.on('nodeselectchange', handleNodeSelectChange);

      return () => {
        if (graph) {
          // Destroy Graph Listeners
          graph.off(GraphCommonEvent.onViewportChange, debounceHideTooltip);
          graph.off(GraphCommonEvent.onAfterModeChange, debounceHideTooltip);
          graph.off(EditorEvent.onAfterRelayout, handleLayoutChange);
          graph.off(EditorEvent.onBeforeRelayout, onBeforeRelayout);
          // Destroy Delete Listeners
          graph.off(`${shapeNames.delete}:click`, handleDeleteClick);
          graph.off(`${shapeNames.delete}:mouseenter`, handleDeleteMouseEnter);
          graph.off(`${shapeNames.delete}:mouseleave`, handleDeleteMouseLeave);
          // Destroy Link Listeners
          graph.off(`${shapeNames.linkIcon}:mouseenter`, handleLinkIconMouseEnter);
          graph.off(`${shapeNames.linkIcon}:mouseleave`, handleLinkIconMouseLeave);
          // Destroy Anchor Listeners
          graph.off(`${shapeNames.anchorOut}:mouseenter`, handleAnchorMouseEnter);
          graph.off(`${shapeNames.anchorOut}:mouseleave`, handleAnchorMouseLeave);
          // Destroy of Users Listeners
          graph.off(`${shapeNames.numUsersGroup}:mouseenter`, handleNumOfUsersMouseEnter);
          graph.off(`${shapeNames.numUsersGroup}:mouseleave`, handleNumOfUsersMouseleave);
          graph.off(
            `${shapeNames.numUsersEnteredGroup}:mouseenter`,
            handleNumOfUsersEnteredMouseEnter,
          );
          graph.off(
            `${shapeNames.numUsersEnteredGroup}:mouseleave`,
            handleNumOfUsersEnteredMouseleave,
          );
          graph.off(
            `${shapeNames.numUsersExitedGroup}:mouseenter`,
            handleNumOfUsersExitedMouseEnter,
          );
          graph.off(
            `${shapeNames.numUsersExitedGroup}:mouseleave`,
            handleNumOfUsersExitedMouseleave,
          );
          // Destroy Edge Listeners
          graph.off('aftercreateedge', handleAfterCreateEdge);
          // Destroy Node Listeners
          graph.off('node:dragend', handleNodeDragEnd);
          graph.off('nodeselectchange', handleNodeSelectChange);
        }
      };
    }, []);

    useEffect(() => {
      if (!graph) return;

      let nodeWithInlineEdit = null;

      nodes.forEach((node) => {
        const { id, isSelected, isInlineEditInProgress } = node;

        if (typeof id !== 'string') {
          if (process.env.NODE_ENV !== 'production') {
            console.warn(
              `Node id in flowchart must be a string, but instead is ${typeof id}:`,
              node,
            );
          }
          return;
        }

        const graphNode = graph.findById(id);

        if (!graphNode || graphNode.destroyed === true) return;

        graph.setItemState(id, ItemState.Selected, isSelected);

        if (isInlineEditInProgress) {
          nodeWithInlineEdit = id;
        }
      });

      // shows inline edit, but only for the last node with `isInlineEditInProgress`
      if (nodeWithInlineEdit) {
        showInlineEdit(graph, nodeWithInlineEdit);
      }
    }, [nodes]);

    return null;
  },
);
